/**
 * Which portal a set of roles belongs to.
 *
 * There are five: the landlord app, the Tenant Portal, the Owner Portal, the
 * Control Centre and the Explore posting area for service providers. The web
 * and the mobile app answer the question from the same list, so a person can
 * never land in one place on a phone and another in a browser.
 *
 * The role lists are a convenience for choosing a destination and a label,
 * not a permission check. The server decides what a role may actually do, on
 * every request.
 */

export const PLATFORM_ROLES = [
  'super_admin', 'operations_admin', 'finance_admin', 'support_admin',
  'marketplace_admin', 'content_admin', 'readonly_admin',
] as const;

/**
 * A tenant's login, which is a different KIND of account rather than a smaller
 * landlord one. It carries no landlord permission at all, so sending one into
 * the landlord app would not show a reduced app — it would show a shell whose
 * every request fails.
 */
export const TENANT_ROLES = ['tenant_user'] as const;

/** An owner's login — like a tenant's, a different kind of account. */
export const OWNER_ROLES = ['owner_user'] as const;

/** Somebody who lists a trade or sells an item in Explore. */
export const PROVIDER_ROLES = ['provider_user'] as const;

export type PortalKind = 'landlord' | 'tenant' | 'owner' | 'platform' | 'provider';

const has = (roles: readonly string[], of: readonly string[]) => roles.some(r => of.includes(r));

/** Which portal these roles belong to. The precedence matches web/src/lib/portal.ts. */
export function portalFor(roles: readonly string[]): PortalKind {
  if (has(roles, TENANT_ROLES)) return 'tenant';
  if (has(roles, OWNER_ROLES)) return 'owner';
  if (has(roles, PROVIDER_ROLES)) return 'provider';
  if (has(roles, PLATFORM_ROLES)) return 'platform';
  return 'landlord';
}

/** Web route roots, so the web's homeFor() and the app's deep links agree. */
export const PORTAL_ROOT = '/portal';
export const OWNER_ROOT = '/owner';
export const PROVIDER_ROOT = '/my-listings';
export const ADMIN_ROOT = '/admin';

/** Where a set of roles belongs on the web. */
export function homeFor(roles: readonly string[]): string {
  switch (portalFor(roles)) {
    case 'tenant': return PORTAL_ROOT;
    case 'owner': return OWNER_ROOT;
    case 'provider': return PROVIDER_ROOT;
    case 'platform': return ADMIN_ROOT;
    default: return '/';
  }
}

/** What to call the portal on a button. */
export function portalLabel(kind: PortalKind): string {
  // A tenant's portal is not a dashboard and calling it one sets the wrong
  // expectation: it is the place their own home, rent and repairs live.
  switch (kind) {
    case 'tenant': return 'My home';
    case 'owner': return 'My portfolio';
    case 'provider': return 'My listings';
    case 'platform': return 'Control Center';
    default: return 'Dashboard';
  }
}
